import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { leadsAPI } from '@/lib/api';
import { Send } from 'lucide-react';

export function LeadCaptureForm({ cardId = 'general', title, description, buttonText = 'Send Message' }) {
  const [formData, setFormData] = useState({
    guest_name: '',
    company_name: '',
    job_title: '',
    phone_number: '',
    guest_email: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.guest_name || !formData.company_name || !formData.job_title || 
        !formData.phone_number || !formData.guest_email) {
      toast.error('נא למלא את כל השדות הנדרשים', {
        description: 'All fields are required',
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.guest_email)) {
      toast.error('כתובת אימייל לא תקינה', {
        description: 'Please enter a valid email address',
      });
      return;
    }

    setLoading(true);
    try {
      await leadsAPI.create({
        card_id: cardId,
        ...formData,
      });

      toast.success('הודעתך נשלחה בהצלחה!', {
        description: 'Thank you for your interest. We will contact you soon.',
        duration: 5000,
      });

      // Reset form
      setFormData({
        guest_name: '',
        company_name: '',
        job_title: '',
        phone_number: '',
        guest_email: '',
        notes: '',
      });
    } catch (err) {
      toast.error('שגיאה בשליחת ההודעה', {
        description: 'Please try again or contact us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle className="text-2xl text-title">{title}</CardTitle>}
          {description && <CardDescription className="text-body">{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Full Name */}
            <div>
              <Label className="text-sm text-title mb-2 block">
                שם מלא <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                name="guest_name"
                value={formData.guest_name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="bg-secondary border-border text-title"
              />
            </div>

            {/* Company Name */}
            <div>
              <Label className="text-sm text-title mb-2 block">
                שם החברה <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Acme Corp"
                required
                className="bg-secondary border-border text-title"
              />
            </div>

            {/* Job Title */}
            <div>
              <Label className="text-sm text-title mb-2 block">
                תפקיד <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                placeholder="Marketing Manager"
                required
                className="bg-secondary border-border text-title"
              />
            </div>

            {/* Phone Number */}
            <div>
              <Label className="text-sm text-title mb-2 block">
                טלפון <span className="text-destructive">*</span>
              </Label>
              <Input
                type="tel"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="050-1234567"
                required
                className="bg-secondary border-border text-title"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <Label className="text-sm text-title mb-2 block">
              אימייל <span className="text-destructive">*</span>
            </Label>
            <Input
              type="email"
              name="guest_email"
              value={formData.guest_email}
              onChange={handleChange}
              placeholder="john@acme.com"
              required
              className="bg-secondary border-border text-title"
            />
          </div>

          {/* Notes/Message */}
          <div>
            <Label className="text-sm text-title mb-2 block">
              הודעה / פרטים נוספים
            </Label>
            <Textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="ספר לנו על הצורך שלך / Tell us about your needs..."
              rows={4}
              className="bg-secondary border-border text-title resize-none"
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-silver sheen-effect laser-engraved gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                שולח...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {buttonText}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
